import { useState } from 'react';
import { Plus, Pencil, Trash2, ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { CurrencyInput } from '@/components/ui/currency-input';
import { Label } from '@/components/ui/label';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  useJackpotStore,
  useJackpotBasicInfo,
  useJackpotEvents,
  type JackpotEventDraft,
} from '@/state_management_and_stores/jackpot.store';
import { useUserOrganizations } from '@/api/extensions/useJackpotOps';
import { formatCurrency } from '@/utils/formatters';
import {
  EditEventDialog,
  type EventFormData,
} from '@/components/delegate/EditEventDialog';
import { getEventDisplayTitle, isTeamRopingEvent } from '@/utils/eventTypes';
import { describeBalancedSplit } from '@/utils/balancedPositions';

/** Parse number input: returns undefined when empty, preserves 0 */
function parseNum(val: string): number | undefined {
  if (val === '') return undefined;
  const n = parseFloat(val);
  return isNaN(n) ? undefined : n;
}

/** Parse integer input: returns undefined when empty, preserves 0 */
function parseIntOpt(val: string): number | undefined {
  if (val === '') return undefined;
  const n = parseInt(val, 10);
  return isNaN(n) ? undefined : n;
}

/** Parse number input with 0 fallback (for required fields like entry_fee) */
function parseNumOrZero(val: string): number {
  const n = parseFloat(val);
  return isNaN(n) ? 0 : n;
}

// ─── Step 1: Basic Info ─────────────────────────────────────────

export function BasicInfoStep() {
  const info = useJackpotBasicInfo();
  const setBasicInfo = useJackpotStore((s) => s.setBasicInfo);
  const { data: orgs, isLoading: orgsLoading } = useUserOrganizations();

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2 md:col-span-2">
          <Label htmlFor="name">Name *</Label>
          <Input
            id="name"
            value={info.name}
            onChange={(e) => setBasicInfo({ name: e.target.value })}
            placeholder="e.g. Spring Classic Jackpot"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="organization">Organization *</Label>
          <Select
            value={info.organization_id}
            onValueChange={(v) => setBasicInfo({ organization_id: v })}
          >
            <SelectTrigger id="organization">
              <SelectValue placeholder={orgsLoading ? 'Loading...' : 'Select organization'} />
            </SelectTrigger>
            <SelectContent>
              {(orgs ?? []).map((org) => (
                <SelectItem key={org.id} value={org.id}>
                  {org.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="type">Type</Label>
          <Select
            value={info.rodeo_or_jackpot}
            onValueChange={(v) =>
              setBasicInfo({ rodeo_or_jackpot: v as 'jackpot' | 'rodeo' })
            }
          >
            <SelectTrigger id="type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="jackpot">Jackpot</SelectItem>
              <SelectItem value="rodeo">Rodeo</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="location">Location</Label>
          <Input
            id="location"
            value={info.location}
            onChange={(e) => setBasicInfo({ location: e.target.value })}
            placeholder="e.g. Cheyenne, WY"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="start_date">Start Date *</Label>
          <DatePicker
            id="start_date"
            value={info.start_date}
            onChange={(val) => setBasicInfo({ start_date: val })}
            placeholder="Select start date"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="end_date">End Date</Label>
          <DatePicker
            id="end_date"
            value={info.end_date}
            min={info.start_date || undefined}
            onChange={(val) => setBasicInfo({ end_date: val })}
            placeholder="Select end date"
          />
        </div>
      </div>
    </div>
  );
}

// ─── Step 2: Events ─────────────────────────────────────────────

export const EVENT_TYPES = ['Team Roping', 'Tie-Down Roping', 'Barrel Racing', 'Steer Wrestling', 'Other'];
const HANDICAP_TYPES = ['combined', 'individual', 'none'];
const SLIDE_TYPES = [
  { value: 'NONE', label: 'Disabled' },
  { value: 'SLIDE_DOWN', label: 'Slide Down Only' },
  { value: 'SLIDE_UP_DOWN', label: 'Slide Up and Down' },
];

function InlineCheckbox({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex items-center gap-2 text-sm cursor-pointer">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="h-4 w-4 rounded border-gray-300"
      />
      {label}
    </label>
  );
}

export function AdvancedEventSettings({
  ev,
  idx,
  updateEvent,
}: {
  ev: JackpotEventDraft;
  idx: number;
  updateEvent: (index: number, event: Partial<JackpotEventDraft>) => void;
}) {
  const [hasPickAndDraw, setHasPickAndDraw] = useState(
    (ev.pick_count != null && ev.pick_count > 0) || (ev.draw_count != null && ev.draw_count > 0)
  );

  // Derived max teams (max_contestants / 2)
  const maxTeams = ev.max_contestants != null ? Math.floor(ev.max_contestants / 2) : undefined;

  const handleMaxTeamsChange = (teams: number | undefined) => {
    if (teams == null) {
      updateEvent(idx, { max_contestants: undefined });
    } else {
      updateEvent(idx, { max_contestants: teams * 2 });
    }
  };

  // Auto prize calc from max_contestants × entry_fee
  const autoPrize = (ev.max_contestants ?? 0) * (ev.entry_fee ?? 0);

  const slideActive = ev.slide_type != null && ev.slide_type !== 'NONE';

  return (
    <div className="space-y-4 pt-2">
      {/* Max Teams & Contestants Row */}
      <fieldset className="space-y-2">
        <legend className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Contestants</legend>
        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1">
            <Label className="text-xs">Max Teams</Label>
            <Input
              type="number" min={1}
              value={maxTeams ?? ''}
              onChange={(e) => handleMaxTeamsChange(parseIntOpt(e.target.value))}
              placeholder="Unlimited"
              className="h-8 text-sm"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Max Contestants</Label>
            <Input
              type="number" min={1}
              value={ev.max_contestants ?? ''}
              onChange={(e) => updateEvent(idx, { max_contestants: parseIntOpt(e.target.value) })}
              placeholder="Unlimited"
              className="h-8 text-sm"
            />
            {ev.max_contestants != null && ev.max_contestants > 0 && (
              <p className="text-xs text-muted-foreground">= {Math.floor(ev.max_contestants / 2)} teams</p>
            )}
          </div>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Contestants per Performance</Label>
          <Input
            type="number" min={1}
            value={ev.contestants_per_performance ?? ''}
            onChange={(e) =>
              updateEvent(idx, { contestants_per_performance: parseIntOpt(e.target.value) })
            }
            placeholder="Auto (split max contestants across performances)"
            className="h-8 text-sm"
          />
          <p className="text-xs text-muted-foreground">
            How many contestants run in each performance. Slack is uncapped — any overflow rolls into slack.
          </p>
        </div>
      </fieldset>

      {/* Handicap Settings */}
      <fieldset className="space-y-2">
        <legend className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Handicap Limits</legend>
        <p className="text-xs text-muted-foreground">Leave empty for unlimited. Values 0-20, increments of 0.5.</p>
        <div className="grid grid-cols-3 gap-2">
          <div className="space-y-1">
            <Label className="text-xs">Max Combined HC</Label>
            <Input
              type="number" min={0} max={20} step={0.5}
              value={ev.max_combined_handicap ?? ''}
              onChange={(e) => updateEvent(idx, { max_combined_handicap: parseNum(e.target.value) })}
              placeholder="No limit"
              className="h-8 text-sm"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Header Max HC</Label>
            <Input
              type="number" min={0} max={20} step={0.5}
              value={ev.header_max_handicap ?? ''}
              onChange={(e) => updateEvent(idx, { header_max_handicap: parseNum(e.target.value) })}
              placeholder="No limit"
              className="h-8 text-sm"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Heeler Max HC</Label>
            <Input
              type="number" min={0} max={20} step={0.5}
              value={ev.heeler_max_handicap ?? ''}
              onChange={(e) => updateEvent(idx, { heeler_max_handicap: parseNum(e.target.value) })}
              placeholder="No limit"
              className="h-8 text-sm"
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1">
            <Label className="text-xs">Max Individual HC</Label>
            <Input
              type="number" min={0} max={20} step={0.5}
              value={ev.max_individual_handicap ?? ''}
              onChange={(e) => updateEvent(idx, { max_individual_handicap: parseNum(e.target.value) })}
              placeholder="No limit"
              className="h-8 text-sm"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Handicap Type</Label>
            <Select
              value={ev.handicap_type || 'combined'}
              onValueChange={(v) => updateEvent(idx, { handicap_type: v })}
            >
              <SelectTrigger className="h-8 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {HANDICAP_TYPES.map((t) => (
                  <SelectItem key={t} value={t}>{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </fieldset>

      {/* Round Configuration */}
      <fieldset className="space-y-2">
        <legend className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Round Configuration</legend>
        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1">
            <Label className="text-xs">Number of Rounds</Label>
            <Select
              value={String(ev.total_rounds ?? 1)}
              onValueChange={(v) => updateEvent(idx, { total_rounds: parseInt(v, 10) })}
            >
              <SelectTrigger className="h-8 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[1,2,3,4,5,6,7,8,9,10].map((n) => (
                  <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Short Round Size</Label>
            <Input
              type="number" min={0}
              value={ev.short_round_size ?? ''}
              onChange={(e) => updateEvent(idx, { short_round_size: parseIntOpt(e.target.value) })}
              placeholder="0"
              className="h-8 text-sm"
            />
            <p className="text-xs text-muted-foreground">Contestants advancing to short round</p>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Short Round Count</Label>
            <Input
              type="number" min={0}
              value={ev.short_round_count ?? ''}
              onChange={(e) => updateEvent(idx, { short_round_count: parseIntOpt(e.target.value) })}
              placeholder="0"
              className="h-8 text-sm"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Guaranteed Runs</Label>
            <Select
              value={String(ev.entry_limits?.guaranteed_runs ?? 1)}
              onValueChange={(v) =>
                updateEvent(idx, {
                  entry_limits: {
                    ...ev.entry_limits,
                    guaranteed_runs: parseInt(v, 10),
                  },
                })
              }
            >
              <SelectTrigger className="h-8 text-sm">
                <SelectValue placeholder="Default" />
              </SelectTrigger>
              <SelectContent>
                {Array.from({ length: ev.total_rounds ?? 1 }, (_, i) => i + 1).map((n) => (
                  <SelectItem key={n} value={String(n)}>
                    {n} {n === 1 ? 'run' : 'runs'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">Min runs before no-time elimination</p>
          </div>
          <div className="flex items-end pb-1">
            <InlineCheckbox
              label="Elimination enabled"
              checked={ev.elimination_enabled ?? false}
              onChange={(v) => updateEvent(idx, { elimination_enabled: v })}
            />
          </div>
        </div>
      </fieldset>

      {/* Fees & Prizes */}
      <fieldset className="space-y-2">
        <legend className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Fees & Prizes</legend>
        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1">
            <Label className="text-xs">Transaction Fee</Label>
            <CurrencyInput
              value={ev.transaction_fee != null ? String(ev.transaction_fee) : ''}
              onChange={(v) => updateEvent(idx, { transaction_fee: parseNum(v) })}
              className="h-8 text-sm"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Total Prize Amount</Label>
            <CurrencyInput
              value={ev.total_prize_amount != null ? String(ev.total_prize_amount) : ''}
              onChange={(v) => updateEvent(idx, { total_prize_amount: parseNum(v) })}
              className="h-8 text-sm"
            />
            {autoPrize > 0 && ev.total_prize_amount !== autoPrize && (
              <button
                type="button"
                className="text-xs text-blue-600 hover:underline"
                onClick={() => updateEvent(idx, { total_prize_amount: autoPrize })}
              >
                Auto-calc: {formatCurrency(autoPrize)} ({ev.max_contestants} × {formatCurrency(ev.entry_fee)})
              </button>
            )}
          </div>
        </div>
      </fieldset>

      {/* Entry Limits */}
      <fieldset className="space-y-2">
        <legend className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Entry Limits</legend>
        <div className="grid grid-cols-2 gap-2">
          <div className="flex items-end pb-1">
            <InlineCheckbox
              label="Allow multiple entries"
              checked={ev.entry_limits?.allow_multiple_entries ?? false}
              onChange={(v) =>
                updateEvent(idx, {
                  entry_limits: { ...ev.entry_limits, allow_multiple_entries: v },
                })
              }
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Max Entries / Contestant</Label>
            <Input
              type="number" min={1}
              value={ev.entry_limits?.max_entries_per_contestant ?? ''}
              onChange={(e) =>
                updateEvent(idx, {
                  entry_limits: {
                    ...ev.entry_limits,
                    max_entries_per_contestant: parseIntOpt(e.target.value),
                  },
                })
              }
              placeholder="Unlimited"
              className="h-8 text-sm"
            />
          </div>
        </div>
      </fieldset>

      {/* Event Configuration (Flags & Partner Options) */}
      <fieldset className="space-y-2">
        <legend className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Event Configuration</legend>
        <div className="grid grid-cols-2 gap-2">
          <InlineCheckbox
            label="Century jackpot (Combined age ≥ 100)"
            checked={ev.is_century_jackpot ?? false}
            onChange={(v) => updateEvent(idx, { is_century_jackpot: v })}
          />
          <InlineCheckbox
            label="Mixed gender (One male, one female)"
            checked={ev.is_mixed_gender ?? false}
            onChange={(v) => updateEvent(idx, { is_mixed_gender: v })}
          />
          <InlineCheckbox
            label="Allow entry without picked partners"
            checked={ev.allow_no_partner_entry ?? false}
            onChange={(v) => updateEvent(idx, { allow_no_partner_entry: v })}
          />
          <InlineCheckbox
            label="Require mutual confirmation for picks"
            checked={ev.enforce_mutual_picks ?? true}
            onChange={(v) => updateEvent(idx, { enforce_mutual_picks: v })}
          />
        </div>
      </fieldset>

      {/* Pick and Draw */}
      <fieldset className="space-y-2">
        <legend className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Pick and Draw</legend>
        <InlineCheckbox
          label="Enable pick and draw rounds"
          checked={hasPickAndDraw}
          onChange={(v) => {
            setHasPickAndDraw(v);
            if (!v) {
              updateEvent(idx, { pick_count: undefined, draw_count: undefined });
            }
          }}
        />
        {hasPickAndDraw && (
          <div className="grid grid-cols-2 gap-2 ml-6 mt-1">
            <div className="space-y-1">
              <Label className="text-xs">Pick Rounds</Label>
              <Input
                type="number" min={0} max={10}
                value={ev.pick_count ?? ''}
                onChange={(e) => updateEvent(idx, { pick_count: parseIntOpt(e.target.value) })}
                placeholder="0"
                className="h-8 text-sm"
              />
              <p className="text-xs text-muted-foreground">Rounds where contestants pick partners</p>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Draw Rounds</Label>
              <Input
                type="number" min={0} max={10}
                value={ev.draw_count ?? ''}
                onChange={(e) => updateEvent(idx, { draw_count: parseIntOpt(e.target.value) })}
                placeholder="0"
                className="h-8 text-sm"
              />
              <p className="text-xs text-muted-foreground">Rounds with random draw</p>
            </div>
          </div>
        )}
      </fieldset>

      {/* Slide System */}
      <fieldset className="space-y-2">
        <legend className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Slide System</legend>
        <div className="grid grid-cols-3 gap-2">
          <div className="space-y-1">
            <Label className="text-xs">Slide Type</Label>
            <Select
              value={ev.slide_type || 'NONE'}
              onValueChange={(v) => updateEvent(idx, { slide_type: v })}
            >
              <SelectTrigger className="h-8 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SLIDE_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              {ev.slide_type === 'SLIDE_DOWN'
                ? 'Teams above base get penalties, below get bonuses'
                : ev.slide_type === 'SLIDE_UP_DOWN'
                ? 'Teams above/below base get penalties/bonuses'
                : 'No slide adjustments'}
            </p>
          </div>
          {slideActive && (
            <>
              <div className="space-y-1">
                <Label className="text-xs">Base Handicap</Label>
                <Input
                  type="number" min={1} max={15} step={0.5}
                  value={ev.slide_base_handicap ?? ''}
                  onChange={(e) => updateEvent(idx, { slide_base_handicap: parseNum(e.target.value) })}
                  placeholder="1-15"
                  className="h-8 text-sm"
                />
                <p className="text-xs text-muted-foreground">Base for slide calculations (1-15, 0.5 inc)</p>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Seconds per 0.5 HC</Label>
                <Input
                  type="number" min={0.5} max={5} step={0.5}
                  value={ev.slide_conversion_rate ?? ''}
                  onChange={(e) => updateEvent(idx, { slide_conversion_rate: parseNum(e.target.value) })}
                  placeholder="0.5"
                  className="h-8 text-sm"
                />
                <p className="text-xs text-muted-foreground">Time adjustment per 0.5 HC difference</p>
              </div>
            </>
          )}
        </div>
        {/* Slide Preview */}
        {slideActive && ev.slide_base_handicap != null && (
          <div className="rounded-md bg-blue-50 border border-blue-200 p-2 text-xs dark:bg-blue-950/30 dark:border-blue-800">
            <p className="font-medium text-blue-900 dark:text-blue-300 mb-1">Example Calculations:</p>
            <div className="space-y-0.5 text-blue-700 dark:text-blue-400">
              <p>Team HC {ev.slide_base_handicap + 2}: +{(2 * 2 * (ev.slide_conversion_rate ?? 0.5)).toFixed(1)}s penalty (above base)</p>
              <p>Team HC {ev.slide_base_handicap}: No adjustment</p>
              <p>Team HC {ev.slide_base_handicap - 2}: -{(2 * 2 * (ev.slide_conversion_rate ?? 0.5)).toFixed(1)}s bonus (below base)</p>
            </div>
          </div>
        )}
      </fieldset>

      {/* Registration Rules */}
      <fieldset className="space-y-2">
        <legend className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Entry Rules</legend>
        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1">
            <Label className="text-xs">Entry Deadline</Label>
            <DatePicker
              value={ev.registration_deadline || ''}
              onChange={(val) => updateEvent(idx, { registration_deadline: val || undefined })}
              placeholder="No deadline"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Max Entries / User</Label>
            <Input
              type="number" min={1}
              value={ev.max_registrations_per_user ?? ''}
              onChange={(e) => updateEvent(idx, { max_registrations_per_user: parseIntOpt(e.target.value) })}
              placeholder="1"
              className="h-8 text-sm"
            />
          </div>
          <div className="flex items-end pb-1">
            <InlineCheckbox
              label="Allow multiple entries"
              checked={ev.allow_multiple_registrations ?? false}
              onChange={(v) => updateEvent(idx, { allow_multiple_registrations: v })}
            />
          </div>
        </div>
        {/* Team roping only, and only once the cap allows a second entry —
            the backend ignores the flag otherwise. */}
        {ev.allow_multiple_registrations &&
          (ev.max_registrations_per_user ?? 1) > 1 &&
          isTeamRopingEvent({ trig_event_type_name: ev.event_type }) && (
            <InlineCheckbox
              label={`Require an even Header / Heeler split (${describeBalancedSplit(ev.max_registrations_per_user ?? 1)})`}
              checked={ev.enforce_balanced_positions ?? false}
              onChange={(v) => updateEvent(idx, { enforce_balanced_positions: v })}
            />
          )}
      </fieldset>
    </div>
  );
}

/**
 * Convert a store-side JackpotEventDraft into the shape EditEventDialog
 * expects. Mainly about folding `rounds[]` (with `prizes[]`) back into
 * `round_prizes[]` (with `places[]`) so the dialog's prize UI lights up.
 */
function draftToEventFormData(ev: JackpotEventDraft): EventFormData {
  const round_prizes =
    Array.isArray(ev.rounds) && ev.rounds.length > 0
      ? ev.rounds.map((r) => {
          const totalAmount = r.round_prize_total ?? 0;
          return {
            round_number: r.round_number,
            has_prizes: !!r.has_prizes || totalAmount > 0,
            round_prize_total: totalAmount,
            places: (r.prizes ?? []).map((p) => ({
              place: p.place,
              // Legacy rounds store `amount`; derive a percentage so the
              // dialog sliders have a starting value.
              percentage: totalAmount > 0 ? Math.round((p.amount / totalAmount) * 100) : 0,
              prize_amount: p.amount,
            })),
          };
        })
      : undefined;

  return {
    id: ev.id,
    event_name: ev.event_name,
    event_type: ev.event_type,
    entry_fee: ev.entry_fee,
    max_contestants: ev.max_contestants,
    contestants_per_performance: ev.contestants_per_performance,
    handicap_type: ev.handicap_type,
    max_combined_handicap: ev.max_combined_handicap,
    header_max_handicap: ev.header_max_handicap,
    heeler_max_handicap: ev.heeler_max_handicap,
    max_individual_handicap: ev.max_individual_handicap,
    total_rounds: ev.total_rounds,
    is_century_jackpot: ev.is_century_jackpot,
    is_mixed_gender: ev.is_mixed_gender,
    total_prize_amount: ev.total_prize_amount,
    transaction_fee: ev.transaction_fee,
    pick_count: ev.pick_count,
    draw_count: ev.draw_count,
    enforce_mutual_picks: ev.enforce_mutual_picks,
    allow_no_partner_entry: ev.allow_no_partner_entry,
    allow_multiple_registrations: ev.allow_multiple_registrations,
    max_registrations_per_user: ev.max_registrations_per_user,
    enforce_balanced_positions: ev.enforce_balanced_positions,
    slide_type: ev.slide_type,
    slide_base_handicap: ev.slide_base_handicap,
    slide_conversion_rate: ev.slide_conversion_rate,
    judge_sheet_template_id: ev.judge_sheet_template_id ?? null,
    ...(round_prizes ? { round_prizes } : {}),
  };
}

/**
 * Fold EditEventDialog's `EventFormData` back into the JackpotEventDraft
 * shape. Preserves fields the dialog doesn't expose by merging over the
 * existing draft when editing.
 */
function eventFormDataToDraft(
  data: EventFormData,
  existing?: JackpotEventDraft,
): JackpotEventDraft {
  const rounds =
    Array.isArray(data.round_prizes) && data.round_prizes.length > 0
      ? data.round_prizes.map((rp) => ({
          round_number: rp.round_number,
          round_type: 'standard',
          has_prizes: !!rp.has_prizes,
          round_prize_total: Number(rp.round_prize_total) || 0,
          prizes: (rp.places ?? []).map((p) => ({
            place: p.place,
            amount: Number(p.prize_amount) || 0,
          })),
        }))
      : (existing?.rounds ?? []);

  // EventFormData has a loose `[key: string]: unknown` index signature which
  // widens every named field's return type. Cast through unknown to line it
  // back up with the tighter JackpotEventDraft shape.
  const d = data as unknown as Partial<JackpotEventDraft> & EventFormData;

  return {
    ...(existing ?? { rounds: [] }),
    id: d.id ?? existing?.id,
    event_name: d.event_name,
    event_type: d.event_type ?? existing?.event_type ?? 'Team Roping',
    entry_fee: Number(d.entry_fee) || 0,
    rounds,
    handicap_type: d.handicap_type ?? existing?.handicap_type,
    header_max_handicap: d.header_max_handicap ?? existing?.header_max_handicap,
    heeler_max_handicap: d.heeler_max_handicap ?? existing?.heeler_max_handicap,
    max_combined_handicap: d.max_combined_handicap ?? existing?.max_combined_handicap,
    max_individual_handicap: d.max_individual_handicap ?? existing?.max_individual_handicap,
    total_rounds: d.total_rounds ?? existing?.total_rounds,
    // max_contestants supports an explicit "Unlimited" by clearing the field.
    // Use the dialog's value verbatim — `??` here would silently restore the
    // prior value when the user blanks the input. Coerce 0 to undefined so a
    // legacy "0" stored as the cap doesn't round-trip back into the draft as
    // a hard cap of zero (which the registration capacity check treats as
    // "always full" and blocks every signup).
    max_contestants:
      d.max_contestants != null && d.max_contestants > 0
        ? d.max_contestants
        : undefined,
    // Same explicit-clear semantics as max_contestants — let the dialog
    // unset the value, and coerce 0/negative to undefined so the CHECK
    // constraint never sees a non-positive write.
    contestants_per_performance:
      d.contestants_per_performance != null && d.contestants_per_performance > 0
        ? d.contestants_per_performance
        : undefined,
    transaction_fee: d.transaction_fee ?? existing?.transaction_fee,
    total_prize_amount: d.total_prize_amount ?? existing?.total_prize_amount,
    pick_count: d.pick_count ?? existing?.pick_count,
    draw_count: d.draw_count ?? existing?.draw_count,
    enforce_mutual_picks: d.enforce_mutual_picks ?? existing?.enforce_mutual_picks,
    allow_no_partner_entry: d.allow_no_partner_entry ?? existing?.allow_no_partner_entry,
    allow_multiple_registrations:
      d.allow_multiple_registrations ?? existing?.allow_multiple_registrations,
    max_registrations_per_user:
      d.max_registrations_per_user ?? existing?.max_registrations_per_user,
    enforce_balanced_positions:
      d.enforce_balanced_positions ?? existing?.enforce_balanced_positions,
    slide_type: d.slide_type ?? existing?.slide_type,
    slide_base_handicap: d.slide_base_handicap ?? existing?.slide_base_handicap,
    slide_conversion_rate: d.slide_conversion_rate ?? existing?.slide_conversion_rate,
    is_century_jackpot: d.is_century_jackpot ?? existing?.is_century_jackpot,
    is_mixed_gender: d.is_mixed_gender ?? existing?.is_mixed_gender,
    // Explicit null clears the override (falls back to auto-classify). `??`
    // would mask a deliberate clear from the dropdown, so use undefined-only
    // fallback semantics.
    judge_sheet_template_id:
      d.judge_sheet_template_id !== undefined
        ? d.judge_sheet_template_id
        : existing?.judge_sheet_template_id,
  };
}

export function EventsStep() {
  const events = useJackpotEvents();
  const { addEvent, updateEvent, removeEvent } = useJackpotStore();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  const openAdd = () => {
    setEditingIndex(null);
    setDialogOpen(true);
  };

  const openEdit = (idx: number) => {
    setEditingIndex(idx);
    setDialogOpen(true);
  };

  const handleSave = (data: EventFormData) => {
    if (editingIndex !== null) {
      const existing = events[editingIndex];
      updateEvent(editingIndex, eventFormDataToDraft(data, existing));
    } else {
      addEvent(eventFormDataToDraft(data));
    }
    setDialogOpen(false);
    setEditingIndex(null);
  };

  const initialEvent: EventFormData | null =
    editingIndex !== null && events[editingIndex]
      ? draftToEventFormData(events[editingIndex])
      : null;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Events</h2>
          <p className="text-sm text-muted-foreground">
            Add each event to this jackpot and configure its rules and prizes.
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={openAdd}>
          <Plus className="h-4 w-4 mr-1" /> Add Event
        </Button>
      </div>

      {events.length === 0 ? (
        <p className="text-sm text-muted-foreground italic py-6 text-center">
          No events yet. Click "Add Event" to create one.
        </p>
      ) : (
        <div className="space-y-2">
          {events.map((ev, idx) => (
            <div
              key={ev.id || `new-${idx}`}
              className="flex items-center gap-3 border rounded-md px-3 py-2.5"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-medium text-sm truncate">
                    {getEventDisplayTitle(ev, 'Unnamed Event')}
                  </p>
                  {ev.event_type && (
                    <Badge variant="secondary" className="text-xs">
                      {ev.event_type}
                    </Badge>
                  )}
                  {ev.rounds.length > 0 && (
                    <Badge variant="outline" className="text-xs">
                      {ev.rounds.length} round{ev.rounds.length !== 1 ? 's' : ''}
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  {ev.entry_fee > 0
                    ? `Entry: ${formatCurrency(ev.entry_fee)}`
                    : 'No entry fee'}
                  {ev.max_contestants != null
                    ? ` \u00b7 Max ${ev.max_contestants}`
                    : ''}
                  {ev.total_prize_amount
                    ? ` \u00b7 Prize ${formatCurrency(ev.total_prize_amount)}`
                    : ''}
                </p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 shrink-0"
                onClick={() => openEdit(idx)}
                aria-label="Edit event"
              >
                <Pencil className="h-3.5 w-3.5" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-destructive hover:text-destructive shrink-0"
                onClick={() => removeEvent(idx)}
                aria-label="Delete event"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          ))}
        </div>
      )}

      <EditEventDialog
        open={dialogOpen}
        onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) setEditingIndex(null);
        }}
        event={initialEvent}
        onSave={handleSave}
      />
    </div>
  );
}

// ─── Step 3: Rounds & Prizes ────────────────────────────────────

const ROUND_TYPES = ['standard', 'short_round'];

export function RoundsAndPrizesStep() {
  const events = useJackpotEvents();
  const { addRound, updateRound, removeRound, addPrize, updatePrize, removePrize } =
    useJackpotStore();
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});

  if (events.length === 0) {
    return (
      <p className="text-sm text-muted-foreground text-center py-6">
        Add events in Step 2 first.
      </p>
    );
  }

  const toggleExpand = (idx: number) =>
    setExpanded((prev) => ({ ...prev, [idx]: !prev[idx] }));

  return (
    <div className="space-y-4">
      {events.map((ev, eIdx) => (
        <Card key={ev.id || eIdx}>
          <CardHeader
            className="p-4 cursor-pointer"
            onClick={() => toggleExpand(eIdx)}
          >
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                {expanded[eIdx] ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
                {getEventDisplayTitle(ev)}
                <Badge variant="outline" className="text-xs">
                  {ev.rounds.length} round{ev.rounds.length !== 1 ? 's' : ''}
                </Badge>
              </CardTitle>
            </div>
          </CardHeader>

          {expanded[eIdx] && (
            <CardContent className="p-4 pt-0 space-y-3">
              {ev.rounds.map((round, rIdx) => (
                <div key={rIdx} className="border rounded-md p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">
                      Round {round.round_number}
                    </span>
                    <div className="flex items-center gap-2">
                      <Select
                        value={round.round_type}
                        onValueChange={(v) =>
                          updateRound(eIdx, rIdx, { round_type: v })
                        }
                      >
                        <SelectTrigger className="h-7 w-32 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {ROUND_TYPES.map((t) => (
                            <SelectItem key={t} value={t}>
                              {t.replace('_', ' ')}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-destructive"
                        onClick={() => removeRound(eIdx, rIdx)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>

                  {/* Round settings */}
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                    <InlineCheckbox
                      label="Progressive"
                      checked={round.is_progressive ?? false}
                      onChange={(v) => updateRound(eIdx, rIdx, { is_progressive: v })}
                    />
                    {round.is_progressive && (
                      <div className="flex items-center gap-1">
                        <Label className="text-xs text-muted-foreground">Advancing:</Label>
                        <Input
                          type="number" min={0}
                          value={round.contestants_advancing ?? ''}
                          onChange={(e) =>
                            updateRound(eIdx, rIdx, {
                              contestants_advancing: parseIntOpt(e.target.value),
                            })
                          }
                          placeholder="All"
                          className="h-7 w-20 text-xs"
                        />
                      </div>
                    )}
                    <InlineCheckbox
                      label="Has prizes"
                      checked={round.has_prizes ?? false}
                      onChange={(v) => updateRound(eIdx, rIdx, { has_prizes: v })}
                    />
                    {round.has_prizes && (
                      <div className="flex items-center gap-1">
                        <Label className="text-xs text-muted-foreground">Prize total:</Label>
                        <Input
                          type="number" min={0}
                          value={round.round_prize_total ?? ''}
                          onChange={(e) =>
                            updateRound(eIdx, rIdx, {
                              round_prize_total: parseNum(e.target.value),
                            })
                          }
                          placeholder="$0"
                          className="h-7 w-24 text-xs"
                        />
                      </div>
                    )}
                  </div>

                  {/* Prizes */}
                  {round.prizes.length > 0 && (
                    <div className="space-y-1">
                      <span className="text-xs text-muted-foreground">Prizes</span>
                      {round.prizes.map((prize, pIdx) => (
                        <div key={pIdx} className="flex items-center gap-2">
                          <span className="text-xs w-12 text-muted-foreground">
                            #{prize.place}
                          </span>
                          <Input
                            type="number"
                            min={0}
                            value={prize.amount || ''}
                            onChange={(e) =>
                              updatePrize(eIdx, rIdx, pIdx, {
                                amount: parseNumOrZero(e.target.value),
                              })
                            }
                            placeholder="Amount"
                            className="h-7 text-xs flex-1"
                          />
                          <Input
                            value={prize.description || ''}
                            onChange={(e) =>
                              updatePrize(eIdx, rIdx, pIdx, {
                                description: e.target.value,
                              })
                            }
                            placeholder="Description"
                            className="h-7 text-xs flex-1"
                          />
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-destructive shrink-0"
                            onClick={() => removePrize(eIdx, rIdx, pIdx)}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-xs"
                    onClick={() =>
                      addPrize(eIdx, rIdx, {
                        place: round.prizes.length + 1,
                        amount: 0,
                      })
                    }
                  >
                    <Plus className="h-3 w-3 mr-1" /> Add Prize
                  </Button>
                </div>
              ))}

              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  addRound(eIdx, {
                    round_number: ev.rounds.length + 1,
                    round_type: 'standard',
                    prizes: [],
                  })
                }
              >
                <Plus className="h-4 w-4 mr-1" /> Add Round
              </Button>
            </CardContent>
          )}
        </Card>
      ))}
    </div>
  );
}

// ─── Step 4: Review ─────────────────────────────────────────────

export function ReviewStep() {
  const info = useJackpotBasicInfo();
  const events = useJackpotEvents();
  const { data: orgs } = useUserOrganizations();

  const orgName = orgs?.find((o) => o.id === info.organization_id)?.name ?? 'Unknown';
  const totalEntryFees = events.reduce((sum, e) => sum + (e.entry_fee || 0), 0);
  const totalPrizes = events.reduce(
    (sum, e) =>
      sum + e.rounds.reduce((rs, r) => rs + r.prizes.reduce((ps, p) => ps + (p.amount || 0), 0), 0),
    0,
  );

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="p-4 space-y-2">
          <h3 className="font-semibold text-sm">Basic Info</h3>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
            <span className="text-muted-foreground">Name</span>
            <span>{info.name || '—'}</span>
            <span className="text-muted-foreground">Organization</span>
            <span>{orgName}</span>
            <span className="text-muted-foreground">Type</span>
            <span className="capitalize">{info.rodeo_or_jackpot}</span>
            <span className="text-muted-foreground">Location</span>
            <span>{info.location || '—'}</span>
            <span className="text-muted-foreground">Dates</span>
            <span>
              {info.start_date || '—'}
              {info.end_date ? ` to ${info.end_date}` : ''}
            </span>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-4 space-y-3">
          <h3 className="font-semibold text-sm">
            Events ({events.length})
          </h3>
          {events.length === 0 ? (
            <p className="text-sm text-muted-foreground">No events added.</p>
          ) : (
            events.map((ev, idx) => {
              const adv: string[] = [];
              if (ev.handicap_type && ev.handicap_type !== 'combined') adv.push(`HC: ${ev.handicap_type}`);
              if (ev.header_max_handicap != null) adv.push(`Header max: ${ev.header_max_handicap}`);
              if (ev.heeler_max_handicap != null) adv.push(`Heeler max: ${ev.heeler_max_handicap}`);
              if (ev.max_combined_handicap != null) adv.push(`Combined max: ${ev.max_combined_handicap}`);
              if (ev.total_rounds != null) adv.push(`${ev.total_rounds} rounds`);
              if (ev.elimination_enabled) adv.push('Elimination');
              if (ev.transaction_fee) adv.push(`Transaction fee: ${formatCurrency(ev.transaction_fee)}`);
              if (ev.total_prize_amount) adv.push(`Prizes: ${formatCurrency(ev.total_prize_amount)}`);
              if (ev.max_contestants) adv.push(`Max: ${ev.max_contestants}`);
              if (ev.registration_deadline) adv.push(`Deadline: ${ev.registration_deadline}`);
              if (ev.slide_type && ev.slide_type !== 'NONE') adv.push(`Slide: ${ev.slide_type}`);
              if (ev.is_century_jackpot) adv.push('Century');
              if (ev.is_mixed_gender) adv.push('Mixed gender');
              if (ev.allow_no_partner_entry) adv.push('No-partner OK');
              if (ev.enforce_mutual_picks === false) adv.push('No mutual picks');
              if (ev.pick_count) adv.push(`${ev.pick_count} pick rds`);
              if (ev.draw_count) adv.push(`${ev.draw_count} draw rds`);
              if (ev.entry_limits?.allow_multiple_entries) adv.push('Multi-entry');

              return (
                <div key={idx} className="border rounded-md p-3 space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm">{getEventDisplayTitle(ev)}</span>
                    <Badge variant="secondary" className="text-xs">{ev.event_type}</Badge>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Entry: {formatCurrency(ev.entry_fee)} | {ev.rounds.length} round{ev.rounds.length !== 1 ? 's' : ''}
                    {ev.rounds.reduce((s, r) => s + r.prizes.length, 0) > 0 &&
                      ` | ${ev.rounds.reduce((s, r) => s + r.prizes.length, 0)} prizes`}
                  </div>
                  {adv.length > 0 && (
                    <div className="text-xs text-muted-foreground pt-1 border-t mt-1">
                      <span className="font-medium">Advanced:</span> {adv.join(' · ')}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </CardContent>
      </Card>

      <div className="flex gap-4 text-sm">
        <Card className="flex-1">
          <CardContent className="p-3 text-center">
            <p className="text-muted-foreground text-xs">Total Entry Fees</p>
            <p className="font-semibold">{formatCurrency(totalEntryFees)}</p>
          </CardContent>
        </Card>
        <Card className="flex-1">
          <CardContent className="p-3 text-center">
            <p className="text-muted-foreground text-xs">Total Prize Money</p>
            <p className="font-semibold">{formatCurrency(totalPrizes)}</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
